// League fixture generation.
//
// Pure: takes the league's shape and its teams, returns the matches. No
// Firestore, so the pairing rules can be reasoned about and tested on their
// own — a schedule that quietly drops a fixture is very hard to spot by eye.

const DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const parseLocal = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Minutes past midnight -> "HH:MM". */
const toHHMM = (mins) =>
  `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const toMins = (t) => {
  const [h, m] = String(t || '0:0').split(':').map(Number);
  return h * 60 + (m || 0);
};

/**
 * Round-robin pairings using the circle method.
 *
 * A bye team is added when the count is odd so every round is the same size;
 * pairings against it are dropped, which is what gives one team a bye that
 * week rather than silently omitting a fixture.
 */
export function roundRobinRounds(teamIds) {
  const ids = [...teamIds];
  const bye = '__bye__';
  if (ids.length % 2 === 1) ids.push(bye);
  const n = ids.length;
  if (n < 2) return [];
  const rounds = [];
  let arr = [...ids];
  for (let r = 0; r < n - 1; r++) {
    const pairs = [];
    for (let i = 0; i < n / 2; i++) {
      const a = arr[i], b = arr[n - 1 - i];
      if (a !== bye && b !== bye) {
        // Alternate which side is listed first so "home" is not always the
        // same team across the season.
        pairs.push(r % 2 === 0 ? [a, b] : [b, a]);
      }
    }
    rounds.push(pairs);
    // Rotate all but the first entry.
    arr = [arr[0], arr[n - 1], ...arr.slice(1, n - 1)];
  }
  return rounds;
}

/** The date in week `week` (0-based from startDate) falling on `dayAbbr`. */
export function dateForWeekDay(startDate, week, dayAbbr) {
  const start = parseLocal(startDate);
  if (!start || DAY_INDEX[dayAbbr] === undefined) return '';
  // Anchor on the week containing startDate, then step to the wanted weekday.
  const d = new Date(start);
  d.setDate(d.getDate() + week * 7);
  const delta = (DAY_INDEX[dayAbbr] - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + delta);
  return fmt(d);
}

/**
 * Lay a season out across weeks, days, slots and courts.
 *
 * Each week takes one round of the rotation. A round's pairings fill the
 * league's slots on its first day, then spill onto the next configured day —
 * so a two-night league genuinely uses both nights rather than overloading one.
 *
 * Returns { matches, unplaced }. unplaced is non-empty when a week's fixtures
 * do not fit the available day/slot/court combinations; the caller shows it
 * rather than dropping games on the floor.
 */
export function buildSchedule({ teams, days, timeSlots, startDate, numWeeks, courts }) {
  const teamIds = (teams || []).map(t => t.id);
  const nameOf = Object.fromEntries((teams || []).map(t => [t.id, t.name || '']));
  const rounds = roundRobinRounds(teamIds);
  const matches = [];
  const unplaced = [];
  if (!rounds.length || !(days || []).length || !(timeSlots || []).length) {
    return { matches, unplaced };
  }
  const courtList = (courts || []).length ? courts : ['court-1'];
  const weeks = Math.max(0, Number(numWeeks) || 0);

  for (let w = 0; w < weeks; w++) {
    // Rotation repeats once every team has played everyone else.
    const round = rounds[w % rounds.length];
    // Every (day, slot, court) this week can host one match.
    const cells = [];
    for (const day of days) {
      for (const slot of timeSlots) {
        for (const court of courtList) cells.push({ day, slot, court });
      }
    }
    round.forEach((pair, i) => {
      const cell = cells[i];
      if (!cell) { unplaced.push({ week: w + 1, pair, names: pair.map(id => nameOf[id]) }); return; }
      const startMins = toMins(cell.slot.startTime);
      const len = Number(cell.slot.lengthMinutes) || 60;
      matches.push({
        week: w + 1,
        date: dateForWeekDay(startDate, w, cell.day),
        day: cell.day,
        startTime: cell.slot.startTime,
        endTime: toHHMM(startMins + len),
        court: cell.court,
        homeTeamId: pair[0], homeTeamName: nameOf[pair[0]] || '',
        awayTeamId: pair[1], awayTeamName: nameOf[pair[1]] || '',
      });
    });
  }
  return { matches, unplaced };
}

// ── Schedule edits ───────────────────────────────────────────────────────────
// Both swaps are expressed as patches rather than mutations, so the caller can
// write them in one batch alongside the reservations that mirror them. A match
// and its reservation must never disagree about when or where it is.

/** Title a reservation carries for a match, kept in one place. */
export const matchTitle = (leagueName, home, away) =>
  `${leagueName} — ${home} v ${away}`;

/**
 * Swap two teams between (or within) matches.
 *
 * `slot` is 'home' or 'away'. Swapping a team with itself, or the two sides of
 * the same match, is a no-op rather than an error — dropping a chip back where
 * it came from should do nothing, not corrupt the fixture.
 */
export function swapTeams(a, aSlot, b, bSlot, leagueName = '') {
  if (a.id === b.id && aSlot === bSlot) return null;
  const idKey = (s) => (s === 'home' ? 'homeTeamId' : 'awayTeamId');
  const nameKey = (s) => (s === 'home' ? 'homeTeamName' : 'awayTeamName');

  // Same match: just exchange its two sides.
  if (a.id === b.id) {
    const next = { ...a, [idKey(aSlot)]: a[idKey(bSlot)], [nameKey(aSlot)]: a[nameKey(bSlot)],
      [idKey(bSlot)]: a[idKey(aSlot)], [nameKey(bSlot)]: a[nameKey(aSlot)] };
    return [{
      matchId: a.id,
      match: { homeTeamId: next.homeTeamId, homeTeamName: next.homeTeamName,
        awayTeamId: next.awayTeamId, awayTeamName: next.awayTeamName },
      reservationId: a.reservationId,
      reservation: { title: matchTitle(leagueName, next.homeTeamName, next.awayTeamName) },
    }];
  }

  const aNext = { ...a, [idKey(aSlot)]: b[idKey(bSlot)], [nameKey(aSlot)]: b[nameKey(bSlot)] };
  const bNext = { ...b, [idKey(bSlot)]: a[idKey(aSlot)], [nameKey(bSlot)]: a[nameKey(aSlot)] };
  return [
    { matchId: a.id,
      match: { homeTeamId: aNext.homeTeamId, homeTeamName: aNext.homeTeamName,
        awayTeamId: aNext.awayTeamId, awayTeamName: aNext.awayTeamName },
      reservationId: a.reservationId,
      reservation: { title: matchTitle(leagueName, aNext.homeTeamName, aNext.awayTeamName) } },
    { matchId: b.id,
      match: { homeTeamId: bNext.homeTeamId, homeTeamName: bNext.homeTeamName,
        awayTeamId: bNext.awayTeamId, awayTeamName: bNext.awayTeamName },
      reservationId: b.reservationId,
      reservation: { title: matchTitle(leagueName, bNext.homeTeamName, bNext.awayTeamName) } },
  ];
}

/**
 * Swap when and where two matches are played — the teams stay put, the fixtures
 * exchange week, date, day, time and court. The reservation moves with it, or
 * the court would stay blocked at the old time.
 */
export function swapMatchSlots(a, b) {
  if (a.id === b.id) return null;
  const when = (m) => ({ week: m.week, date: m.date, day: m.day,
    startTime: m.startTime, endTime: m.endTime, court: m.court });
  const aw = when(a), bw = when(b);
  const resOf = (w) => ({ date: w.date, day: w.day, startTime: w.startTime,
    endTime: w.endTime, courts: [w.court], notes: `League week ${w.week}` });
  return [
    { matchId: a.id, match: bw, reservationId: a.reservationId, reservation: resOf(bw) },
    { matchId: b.id, match: aw, reservationId: b.reservationId, reservation: resOf(aw) },
  ];
}
